import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, DownloadSimple, Trash, ArrowSquareOut, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import SendGmailModal from "../components/SendGmailModal";
import { OwnerPill } from "../lib/usersMap";

const STATUS_COLOR = {
  draft: "#717680", sent: "#3A86FF", viewed: "#7C3AED", accepted: "#2A9D8F", rejected: "#D90429",
};

export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [sendTarget, setSendTarget] = useState(null); // proposal object
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/proposals");
    setProposals(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete proposal?")) return;
    await api.delete(`/proposals/${id}`);
    toast.success("Deleted");
    load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/p/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  const calcTotal = (p) => {
    const sub = (p.items || []).reduce((s, i) => s + (Number(i.quantity || 0) * Number(i.unit_price || 0)), 0);
    const afterDisc = sub - Number(p.discount || 0);
    return afterDisc * (1 + Number(p.tax_percent || 0) / 100);
  };

  return (
    <Layout
      title="Proposals"
      subtitle={`${proposals.length} proposal${proposals.length === 1 ? "" : "s"}`}
      actions={
        <button onClick={() => navigate("/proposals/new")} data-testid="new-proposal-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] transition-all active:scale-95">
          <Plus size={16} weight="bold" /> New Proposal
        </button>
      }
    >
      {proposals.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]" data-testid="empty-proposals">
          <FileText size={36} className="mx-auto text-[#D90429]" />
          <p className="mt-4">No proposals yet. Build your first AI-drafted, Shelvestech-branded proposal.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="proposals-grid">
          {proposals.map((p) => (
            <div key={p.id} className="border border-black/10 bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`proposal-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="overline">{p.status}</span>
                <div className="flex items-center gap-2">
                  <OwnerPill userId={p.user_id} />
                  <span className="inline-block w-2 h-2" style={{ background: STATUS_COLOR[p.status] }} />
                </div>
              </div>
              <h3 onClick={() => navigate(`/proposals/${p.id}`)} className="font-display text-xl font-bold tracking-tight mt-3 cursor-pointer hover:text-[#D90429] line-clamp-2">{p.title}</h3>
              <div className="mt-1 text-sm text-[#4A4D57]">{p.client_name} {p.client_company && `· ${p.client_company}`}</div>
              <div className="mt-5 pt-4 border-t border-black/5 flex items-end justify-between">
                <div>
                  <div className="overline text-[10px]">Total</div>
                  <div className="font-display text-2xl font-black tracking-tighter">₹{calcTotal(p).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
                </div>
                <div className="flex gap-1">
                  <button title="Send via Gmail" onClick={() => setSendTarget(p)} className="p-2 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`send-gmail-${p.id}`}><PaperPlaneTilt size={14} weight="bold" /></button>
                  <button title="Open public link" onClick={() => copyLink(p.public_token)} className="p-2 hover:bg-black/5" data-testid={`copy-link-${p.id}`}><ArrowSquareOut size={14} /></button>
                  <a title="Download PDF" href={`${API}/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer" className="p-2 hover:bg-black/5" data-testid={`pdf-${p.id}`}><DownloadSimple size={14} /></a>
                  <button title="Delete" onClick={() => remove(p.id)} className="p-2 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-proposal-${p.id}`}><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SendGmailModal
        open={!!sendTarget}
        onOpenChange={(o) => !o && setSendTarget(null)}
        endpoint={sendTarget ? `/oauth/gmail/send/proposal/${sendTarget.id}` : ""}
        defaultTo={sendTarget?.client_email || ""}
        defaultSubject={sendTarget ? `Proposal · ${sendTarget.title || "Shelvestech Sales Proposal"}` : ""}
        defaultBody={sendTarget ? `Dear ${sendTarget.client_name || "Sir/Madam"},

Please find attached our proposal for "${sendTarget.title || "your enquiry"}".

We look forward to your feedback. Reach out for any clarification on commercials or scope.

Warm regards,` : ""}
        attachmentName={sendTarget ? `Proposal_${(sendTarget.client_name || "client").replace(/\s+/g, "_")}.pdf` : "proposal.pdf"}
        kindLabel="Proposal"
        onSent={() => { toast.success("Proposal emailed"); load(); }}
      />
    </Layout>
  );
}
